import { useCallback } from 'react';
import { useCopyToClipboard } from 'usehooks-ts';

import { isString } from 'lodash';

import { useAccountTimezone, useBuildAccountUrl } from '@/lib/accounts/context';
import { useContextMenu } from '@/components/context-menu/context-menu-context';
import { DropdownMenuItem } from '@/components/dropdown';
import { useToastContext } from '@/components/toast';
import { isValidLink } from '@/explore/utils';
import { useExplorationCellContext } from '@/explore/exploration/exploration-cell-context';
import { Icon } from '@/components/icon';
import {
  buildFilterFromGroupFilter,
  drillDownFromAggregatingOperation,
  findOperationIntroducingAggregatingProperty,
  GroupFilter,
} from '@/explore/utils/drilldown';
import { isRecordsCell } from '@/core/cell';
import { flattenPipeline } from '@/explore/pipeline/utils';
import { isGroupAggregateOperation } from '@/explore/pipeline/operation';
import { useExplorationUIContext } from '@/explore/exploration/exploration-ui-context';
import { useExplorationContext } from '@/explore/exploration/exploration-context';

import { DataTableProperty, DataTableRow } from '../types';
import {
  buildCopyValueMenuItem,
  buildFilterMenuItem,
  buildLinkMenuItem,
  buildOpenDetailViewMenuItem,
} from './menu-items';

export interface OpenCellContextMenuParams {
  position: {
    x: number;
    y: number;
  };
  row: DataTableRow;
  property: DataTableProperty;
  value: unknown;
  canEditPipeline: boolean;
  groupFilters: GroupFilter[];
}

export const useCellContextMenu = () => {
  const [, copyToClipboard] = useCopyToClipboard();
  const { openContextMenu } = useContextMenu();
  const addToast = useToastContext();
  const timezone = useAccountTimezone();
  const buildAccountUrl = useBuildAccountUrl();
  const { exploration, drilldownFromPipeline } = useExplorationContext();
  const { openEditor, openAddForm, closeEditor, scrollToCell } = useExplorationUIContext();
  const { cell } = useExplorationCellContext();

  const buildMenu = useCallback(
    (
      property: DataTableProperty,
      value: unknown,
      row: DataTableRow,
      canEditPipeline: boolean,
      groupFilters: GroupFilter[],
    ) => {
      const menuItems: DropdownMenuItem[] = [];

      if (property.buildLink !== undefined && value !== null) {
        const link = buildAccountUrl(property.buildLink(row));
        menuItems.push(buildOpenDetailViewMenuItem(property, link, closeEditor, scrollToCell));
      }

      if (canEditPipeline) {
        menuItems.push(
          buildFilterMenuItem(cell.id, openEditor, openAddForm, property, value, timezone),
        );
      }

      if (property.isAggregated === true) {
        menuItems.push({
          label: 'Show drilldown',
          onClick: () => {
            if (!isRecordsCell(cell)) {
              throw new Error('Cell is not a records cell');
            }

            const operation = findOperationIntroducingAggregatingProperty(
              flattenPipeline(cell.pipeline, exploration),
              property.key,
            );
            if (operation === undefined) {
              throw new Error(
                `No operation found introducing aggregating property ${property.key}`,
              );
            }

            if (isGroupAggregateOperation(operation)) {
              // Extract group filters from the operation
              groupFilters = groupFilters.concat(
                operation.parameters.groups.map((group) => ({
                  key: group.key,
                  value: row[group.key],
                  precision: isString(group.precision) ? group.precision : undefined,
                })),
              );
            }

            const { pipeline: drilldownPipeline, sort } = drillDownFromAggregatingOperation({
              key: property.key,
              filterOperations: groupFilters.map((groupFilter) =>
                buildFilterFromGroupFilter({
                  field: property,
                  groupFilter,
                  timezone,
                }),
              ),
              pipeline: cell.pipeline,
              exploration,
            });

            drilldownFromPipeline({
              pipeline: drilldownPipeline,
              title: cell.title ?? undefined,
              afterCellId: cell.id,
              sort,
            });
          },
          icon: <Icon name="ExternalLink" size={16} />,
        });
      }

      menuItems.push(buildCopyValueMenuItem(property, value, timezone, addToast, copyToClipboard));

      if (isValidLink(value)) {
        menuItems.push(buildLinkMenuItem(value));
      }

      return menuItems;
    },
    [
      timezone,
      addToast,
      copyToClipboard,
      buildAccountUrl,
      closeEditor,
      scrollToCell,
      cell,
      openEditor,
      openAddForm,
      exploration,
      drilldownFromPipeline,
    ],
  );

  return useCallback(
    (params: OpenCellContextMenuParams) => {
      const { position, row, property, value, canEditPipeline, groupFilters } = params;
      openContextMenu({
        position,
        items: buildMenu(property, value, row, canEditPipeline, groupFilters),
      });
    },
    [openContextMenu, buildMenu],
  );
};
